import { create } from 'zustand'
import { setAutoFreeze } from 'immer'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import type { NextRaceBet } from 'src/types/domain/NextRaceBet'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'

setAutoFreeze(false)

interface NextRaceBetState {
  isPlacingNextRaceBet: boolean
  nextRaceBet?: NextRaceBet
}

export interface NextRaceBetActions {
  setIsPlacingNextRaceBet: (value: boolean) => void
  setNextRaceBet: (bet: Omit<NextRaceBet, 'gameId'> & { gameId: string }) => void
  clearNextRaceBet: () => void
  clearNextRaceBetIfGameChanged: (currentGameId: string) => void
}

type NextRaceBetStore = NextRaceBetState & { actions: NextRaceBetActions }

export const useNextRaceBetStore = create<NextRaceBetStore>()(
  devtools(
    immer((set) => ({
      isPlacingNextRaceBet: true,
      nextRaceBet: undefined,
      actions: {
        setIsPlacingNextRaceBet: (value: boolean) => {
          set(
            (state) => {
              state.isPlacingNextRaceBet = value
            },
            undefined,
            'setIsPlacingNextRaceBet'
          )
        },
        setNextRaceBet: (bet: NextRaceBet) => {
          set(
            (state) => {
              state.nextRaceBet = bet
            },
            undefined,
            'setNextRaceBet'
          )
        },
        clearNextRaceBet: () => {
          set(
            (state) => {
              state.nextRaceBet = undefined
              state.isPlacingNextRaceBet = false
            },
            undefined,
            'clearNextRaceBet'
          )
        },
        clearNextRaceBetIfGameChanged: (currentGameId: string) => {
          set(
            (state) => {
              if (state.nextRaceBet && state.nextRaceBet.gameId !== currentGameId) {
                state.nextRaceBet = undefined
                state.isPlacingNextRaceBet = false
              }
            },
            undefined,
            'clearNextRaceBetIfGameChanged'
          )
        }
      }
    })),
    { name: 'NextRaceBetStore', enabled: isDevtoolsEnabled() }
  )
)

export const useNextRaceBetActions = () => useNextRaceBetStore((state) => state.actions)

export const useGetIsPlacingNextRaceBet = () =>
  useNextRaceBetStore((state) => state.isPlacingNextRaceBet)

export const useGetNextRaceBet = () => useNextRaceBetStore((state) => state.nextRaceBet)
